import { useCallback, useRef } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { BREAKPOINT_LG_QUERY, useMediaQuery } from "@njbsoft/form-ui";
import { type components } from "@njbsoft/types/field";
import { EmptyState, PageHeader, Skeleton } from "@/components/ui";
import { selectThreadId } from "@/components/messages/select-thread";
import { ThreadList } from "@/components/messages/thread-list";
import { TesterThreadPane } from "@/components/tester/messages/tester-thread-pane";
import { useFieldMessageThreads } from "@/hooks/use-field-message-threads";
import { HOME_THREAD_PARAMS } from "@/hooks/use-home-messages";

type MessageThread = components["schemas"]["MessageThread"];

interface MessagesSearch {
  /** The conversation to open on arrival, when the caller came from one. */
  thread?: string;
}

export const Route = createFileRoute("/_company/messages/")({
  // The deep-link contract every "open this conversation" affordance uses: Home's
  // messages strip and the rejection banner's message-utility action both land
  // here with the thread they mean. An unparseable value is dropped rather than
  // rejected — a mangled link should still reach the inbox.
  validateSearch: (search: Record<string, unknown>): MessagesSearch => ({
    thread: typeof search.thread === "string" && search.thread !== "" ? search.thread : undefined,
  }),
  component: TesterMessagesPage,
});

/**
 * Messages — every conversation a tester holds about their work, in one place:
 * both lanes, the utility's and the customer's. Threads are anchored to the
 * work item they concern; this is the one screen that gathers them.
 *
 * The list and the Messages nav badge read the SAME params object, so the count
 * on the badge and the conversations under it are one fetch and one number.
 * The PANE does not read from that list at all — it reads the selected thread
 * by id — so a deep link to a conversation outside the loaded page still opens.
 *
 * Below `lg` the conversation stacks under the list, so a pick scrolls it into
 * view. Only a pick does: the arrival's auto-selection is derived, not handled,
 * so the page opens where it is.
 */
function TesterMessagesPage() {
  const { t } = useTranslation(["field-work"]);
  const navigate = useNavigate();
  const { thread: requestedThreadId } = Route.useSearch();

  const query = useFieldMessageThreads(HOME_THREAD_PARAMS);
  const threads = query.data?.items ?? [];
  const selectedThreadId = selectThreadId(threads, requestedThreadId);
  const besideList = useMediaQuery(BREAKPOINT_LG_QUERY, true);
  const paneRef = useRef<HTMLDivElement>(null);

  // Selection rides the URL so the pane is linkable and survives a reload; the
  // replace keeps a browse through six conversations out of the back stack.
  const handleSelect = useCallback(
    (thread: MessageThread) => {
      void navigate({ to: "/messages", search: { thread: thread.id }, replace: true });
      if (!besideList) paneRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    },
    [navigate, besideList],
  );

  return (
    <div className="space-y-6 p-4 sm:p-8">
      <PageHeader
        title={t("field-work:messages.title")}
        subtitle={t("field-work:messages.subtitle")}
      />

      {query.isError ? (
        <p role="status" className="text-sm text-danger">
          {t("field-work:messages.list.loadError")}
        </p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <ThreadList
            threads={threads}
            selectedThreadId={selectedThreadId}
            isLoading={query.isLoading}
            onSelect={handleSelect}
          />
          <div ref={paneRef} className="min-w-0 scroll-mt-4">
            {selectedThreadId ? (
              <TesterThreadPane key={selectedThreadId} threadId={selectedThreadId} />
            ) : query.isLoading ? (
              // Which conversation opens is not known until the list lands, and
              // the empty state below is a TERMINAL answer — showing it mid-fetch
              // tells a tester with conversations that they have none.
              <Skeleton height="384px" />
            ) : (
              <EmptyState
                title={t("field-work:messages.anchor.title")}
                description={t("field-work:messages.anchor.description")}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
